import { useState, useCallback } from 'react';
import { analyticsService } from '../services/analyticsService';
import { publicAnalyticsService } from '../api/publicApiClient';
import apiClient from '../api/apiClient';
import { analyticsApiClient } from '../api/analyticsApi';
import type {
  DrillToDetailContext,
  DrillToDetailSortState,
  DrillToDetailColumn,
} from '../types/drillToDetail';
import type { QueryFilter } from '../types/analytics';

interface UseDrillToDetailOptions {
  /** Default page size */
  pageSize?: number;
  /** Enable debug mode to see SQL */
  debug?: boolean;
}

interface UseDrillToDetailReturn {
  /** Current data */
  data: Record<string, unknown>[];
  /** Column definitions */
  columns: DrillToDetailColumn[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: string | null;
  /** Current page (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total row count */
  totalRows: number;
  /** Whether there are more pages */
  hasMore: boolean;
  /** Current sort state */
  sortState: DrillToDetailSortState | null;
  /** Fetch data for a specific page */
  fetchPage: (page: number) => Promise<void>;
  /** Sort by a column */
  sortBy: (field: string) => Promise<void>;
  /** Reset state */
  reset: () => void;
  /** Initialize with a context */
  initialize: (context: DrillToDetailContext) => Promise<void>;
}

/**
 * Hook for managing drill-to-detail data fetching
 *
 * Handles pagination, sorting, and state management for drill-to-detail views.
 */
export function useDrillToDetail(
  options: UseDrillToDetailOptions = {}
): UseDrillToDetailReturn {
  const { pageSize = 100, debug = false } = options;

  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [columns, setColumns] = useState<DrillToDetailColumn[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [sortState, setSortState] = useState<DrillToDetailSortState | null>(null);
  const [currentContext, setCurrentContext] = useState<DrillToDetailContext | null>(null);

  const totalPages = Math.ceil(totalRows / pageSize);

  const buildFilters = useCallback(
    (context: DrillToDetailContext): QueryFilter[] => {
      const filters: QueryFilter[] = [];

      // Add existing widget filters
      if (context.existingFilters && context.existingFilters.length > 0) {
        filters.push(...context.existingFilters);
      }

      // Add click filter (e.g., clicked on "Male" segment)
      if (context.clickFilter) {
        filters.push(context.clickFilter);
      }

      return filters;
    },
    []
  );

  // Fetch data for report builder widgets via the report builder query API
  const fetchReportBuilderData = useCallback(
    async (
      context: DrillToDetailContext,
      page: number,
      sort: DrillToDetailSortState | null
    ): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const offset = (page - 1) * pageSize;

        // Step 1: Fetch the saved report config
        const reportResponse = await apiClient.get(`/report-builder/${context.reportBuilderId}`);
        const report = reportResponse.data?.data || reportResponse.data;
        const querySpec = report?.query_config || report?.query_spec;

        if (!querySpec) {
          setError('Report has no query configuration');
          setIsLoading(false);
          return;
        }

        // Step 2: Execute the query with pagination/sorting.
        // Strip saved scoping_type to prevent 403 when viewer has different
        // permissions than the report creator; use explicit scoping from widget.
        const { scoping_type: _savedScope, ...specWithoutScope } = querySpec;
        const queryPayload: any = {
          ...specWithoutScope,
          limit: pageSize,
          offset,
          ...(context.scoping ? { scoping_type: context.scoping } : {}),
        };

        if (sort) {
          queryPayload.order_by = [{ field: sort.field, direction: sort.direction }];
        }

        const queryResponse = await analyticsApiClient.post('/report-builder/query', queryPayload);
        const result = queryResponse.data;

        setData(result.data || result.rows || []);

        // Normalize columns from report builder response
        if (result.schema?.columns && result.schema.columns.length > 0) {
          const normalizedColumns = result.schema.columns
            .filter((col: any) => !col.hidden)
            .map((col: any) => ({
              name: col.key,
              label: col.label,
              data_type: (col.type || 'STRING').toUpperCase(),
              sortable: col.sortable ?? true,
            }));
          setColumns(normalizedColumns);
        } else if (result.columns && result.columns.length > 0) {
          const normalizedColumns = result.columns
            .filter((col: any) => !col.hidden)
            .map((col: any) => ({
              name: col.key || col.name,
              label: col.label,
              data_type: (col.type || col.data_type || 'STRING').toUpperCase(),
              sortable: col.sortable ?? true,
            }));
          setColumns(normalizedColumns);
        }

        const metadata: any = result.meta || result.metadata;
        setTotalRows(metadata?.total_rows || metadata?.totalRows || result.data?.length || 0);
        setHasMore(metadata?.has_more || metadata?.hasMore || false);
        setCurrentPage(metadata?.page || page);
      } catch (err) {
        console.error('[DrillToDetail] Error fetching report builder data:', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'error' in err
              ? (err as { error: string }).error
              : 'Failed to fetch detail data';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize]
  );

  const fetchData = useCallback(
    async (
      context: DrillToDetailContext,
      page: number,
      sort: DrillToDetailSortState | null
    ): Promise<void> => {
      // Route to report builder fetch path when reportBuilderId is present
      if (context.reportBuilderId) {
        return fetchReportBuilderData(context, page, sort);
      }

      setIsLoading(true);
      setError(null);

      try {
        const filters = buildFilters(context);
        const offset = (page - 1) * pageSize;

        const queryConfig: any = {
          metric_key: context.metricKey,
          detailed: true,
          limit: pageSize,
          offset,
          ...(context.scoping && { scoping_type: context.scoping }),
        };

        if (filters.length > 0) {
          queryConfig.filters = filters;
        }

        if (context.timeRange) {
          queryConfig.time_range = context.timeRange;
        }

        if (sort) {
          // Convert camelCase field back to snake_case for the API
          const snakeField = sort.field.replace(/[A-Z]/g, (l) => `_${l.toLowerCase()}`);
          queryConfig.order_by = [{ field: snakeField, direction: sort.direction }];
        }

        console.log('[DrillToDetail] Fetching data:', {
          metricKey: context.metricKey,
          page,
          offset,
          filters,
          sort,
        });

        // Use public API when public context is available, otherwise use authenticated API
        const response = context.publicContext
          ? await publicAnalyticsService.fetchWidgetData<Record<string, unknown>>(
              queryConfig,
              context.publicContext.ownerUserId,
              context.publicContext.accountId,
              context.publicContext.shareToken
            )
          : await analyticsService.fetchWidgetData<Record<string, unknown>>(
              queryConfig,
              { debug }
            );

        // Set data from response
        setData(response.data || []);

        // Handle columns — two formats:
        // V2 format: schema.columns[] with { key, label, type, sortable, hidden }
        // Legacy format (public API after snakeToCamel): columns[] with { name, label, dataType, sortable, hidden }
        // Both snakeToCamel data row KEYS to camelCase, so column names must also be camelCase.
        const toCamel = (s: string) => s.replace(/_([a-z])/g, (_, l) => l.toUpperCase());

        if (response.schema?.columns && response.schema.columns.length > 0) {
          // V2 format
          const normalizedColumns = response.schema.columns
            .filter((col: any) => !col.hidden)
            .map((col: any) => ({
              name: toCamel(col.key),
              label: col.label,
              data_type: col.type.toUpperCase(),
              sortable: col.sortable ?? true,
            }));
          setColumns(normalizedColumns);
        } else if ((response as any).columns && (response as any).columns.length > 0) {
          // Legacy format (public API — snakeToCamel converts data_type → dataType)
          const normalizedColumns = (response as any).columns
            .filter((col: any) => !col.hidden)
            .map((col: any) => ({
              name: toCamel(col.name),
              label: col.label,
              data_type: (col.dataType || col.data_type || 'STRING').toUpperCase(),
              sortable: col.sortable ?? true,
            }));
          setColumns(normalizedColumns);
        }

        // Handle metadata — V2 uses meta (snake_case), legacy/public uses metadata (camelCase after snakeToCamel)
        const metadata: any = response.meta || (response as any).metadata;
        setTotalRows(metadata?.total_rows || metadata?.totalRows || response.data?.length || 0);
        setHasMore(metadata?.has_more || metadata?.hasMore || false);
        setCurrentPage(metadata?.page || page);

        console.log('[DrillToDetail] Data fetched:', {
          rowCount: response.data?.length,
          totalRows: metadata?.total_rows || metadata?.totalRows,
          hasMore: metadata?.has_more,
          columns: response.schema?.columns?.length,
        });
      } catch (err) {
        console.error('[DrillToDetail] Error fetching data:', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'error' in err
              ? (err as { error: string }).error
              : 'Failed to fetch detail data';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [buildFilters, pageSize, debug, fetchReportBuilderData]
  );

  const initialize = useCallback(
    async (context: DrillToDetailContext): Promise<void> => {
      setCurrentContext(context);
      setSortState(null);
      setCurrentPage(1);
      await fetchData(context, 1, null);
    },
    [fetchData]
  );

  const fetchPage = useCallback(
    async (page: number): Promise<void> => {
      if (!currentContext) {
        console.warn('[DrillToDetail] Cannot fetch page without context');
        return;
      }
      setCurrentPage(page);
      await fetchData(currentContext, page, sortState);
    },
    [currentContext, sortState, fetchData]
  );

  const sortBy = useCallback(
    async (field: string): Promise<void> => {
      if (!currentContext) {
        console.warn('[DrillToDetail] Cannot sort without context');
        return;
      }

      // Toggle sort direction or set new field
      const newSort: DrillToDetailSortState = {
        field,
        direction:
          sortState?.field === field && sortState.direction === 'asc' ? 'desc' : 'asc',
      };

      setSortState(newSort);
      setCurrentPage(1);
      await fetchData(currentContext, 1, newSort);
    },
    [currentContext, sortState, fetchData]
  );

  const reset = useCallback(() => {
    setData([]);
    setColumns([]);
    setIsLoading(false);
    setError(null);
    setCurrentPage(1);
    setTotalRows(0);
    setHasMore(false);
    setSortState(null);
    setCurrentContext(null);
  }, []);

  return {
    data,
    columns,
    isLoading,
    error,
    currentPage,
    totalPages,
    totalRows,
    hasMore,
    sortState,
    fetchPage,
    sortBy,
    reset,
    initialize,
  };
}

export default useDrillToDetail;
